import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";
const AUTH_IMAGE = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/1y57ncte_LOGIN.webp";

export const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    
    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(name, email, password);
      // Login automatically after registration
      const userData = await login(email, password);
      toast.success("Conta criada com sucesso!");
      
      // Check if user needs to select a plan (new users are not active)
      if (!userData.is_active) {
        // Force navigation to select-plan for new users
        window.location.href = "/select-plan";
      } else {
        // Existing active users go to dashboard
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao criar conta";
      toast.error(message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-2" data-testid="register-page">
      {/* Mobile Image - Above form */}
      <div 
        className="lg:hidden h-40 sm:h-52 bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: `url(${AUTH_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </div>
      
      {/* Desktop Image Side */}
      <div 
        className="hidden lg:block bg-cover bg-center"
        style={{ backgroundImage: `url(${AUTH_IMAGE})` }}
      />
      
      {/* Form Side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8 lg:mb-12">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              data-testid="back-link"
            >
              <ArrowLeft size={16} />
              {t("back")}
            </Link>
            <LanguageSelector />
          </div>
          
          <div className="mb-8 lg:mb-12">
            <img src={LOGO_URL} alt="AESTHA" className="h-10 lg:h-12 invert mb-6 lg:mb-8" />
            <h1 className="font-serif text-2xl lg:text-3xl mb-2">{t("create_account")}</h1>
            <p className="text-sm lg:text-base text-muted-foreground">
              {t("fill_data")}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
            <div>
              <Label className="label-mono">{t("full_name")}</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-paper"
                placeholder="Seu nome"
                required
                data-testid="name-input"
              />
            </div>
            
            <div>
              <Label className="label-mono">{t("email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-paper"
                placeholder="seu@email.com"
                required
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label className="label-mono">{t("password")}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-paper pr-12"
                  placeholder={t("min_characters")}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <div>
              <Label className="label-mono">{t("confirm_password")}</Label>
              <Input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-paper"
                placeholder={t("confirm_password")}
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? t("creating_account") : t("create_account")}
            </Button>
          </form>
          
          <p className="mt-6 lg:mt-8 text-center text-sm text-muted-foreground">
            {t("have_account")}{" "}
            <Link to="/login" className="text-foreground hover:underline" data-testid="login-link">
              {t("login")}
            </Link>
          </p>
          
          <p className="mt-4 text-center text-xs text-muted-foreground">
            {t("wait_activation")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
